import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Trash2, Store, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export default function RestaurantsPage() {
  const { authHeaders, API } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRestaurants = async () => {
    try {
      const res = await axios.get(`${API}/restaurants`, { headers: authHeaders() });
      setRestaurants(res.data);
    } catch (err) {
      console.error('Failed to fetch restaurants', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchRestaurants(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) { toast.error('Enter a restaurant name'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/restaurants`, {
        name: newName.trim(),
        description: newDesc.trim()
      }, { headers: authHeaders() });
      setRestaurants([res.data, ...restaurants]);
      setNewName('');
      setNewDesc('');
      setShowAdd(false);
      toast.success('Restaurant added!');
    } catch (err) {
      toast.error('Failed to add restaurant');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/restaurants/${deleteTarget}`, { headers: authHeaders() });
      setRestaurants(restaurants.filter(r => r.id !== deleteTarget));
      toast.success('Restaurant removed');
    } catch (err) {
      toast.error('Failed to delete');
    }
    setDeleteTarget(null);
  };

  const formatDate = (dateStr) => {
    try { return format(parseISO(dateStr), "MMM do, yyyy"); }
    catch { return ''; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-lg" style={{ color: '#5E646E' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="restaurants-page" className="pb-nav px-4 md:px-6 max-w-2xl mx-auto fade-in">
      <div className="pt-6 pb-4 flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#1A1D21' }}>
            Restaurants
          </h1>
          <p className="text-sm mt-1" style={{ color: '#5E646E' }}>{restaurants.length} on property</p>
        </div>
        <Button
          data-testid="add-restaurant-button"
          onClick={() => setShowAdd(true)}
          className="h-12 rounded-full px-5 text-base font-medium touch-active gap-2"
          style={{ background: '#005A8D', color: '#FFFFFF' }}
        >
          <Plus className="w-5 h-5" /> Add
        </Button>
      </div>

      {/* Restaurant List */}
      {restaurants.length === 0 ? (
        <div className="bg-white rounded-2xl border p-8 text-center" style={{ borderColor: '#E8E6E0' }}>
          <Store className="w-10 h-10 mx-auto mb-3" style={{ color: '#005A8D' }} />
          <p className="text-base font-medium" style={{ color: '#1A1D21' }}>No restaurants yet</p>
          <p className="text-sm mt-1" style={{ color: '#5E646E' }}>Add the restaurants on the property</p>
        </div>
      ) : (
        <div className="space-y-3">
          {restaurants.map((rest, i) => (
            <div
              key={rest.id}
              data-testid={`restaurant-card-${i}`}
              className="bg-white rounded-2xl border p-4 card-hover flex items-center justify-between"
              style={{ borderColor: '#E8E6E0' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0, 90, 141, 0.08)' }}>
                  <Store className="w-5 h-5" style={{ color: '#005A8D' }} />
                </div>
                <div>
                  <h3 className="text-base font-medium" style={{ color: '#1A1D21' }}>{rest.name}</h3>
                  {rest.description && (
                    <p className="text-sm" style={{ color: '#5E646E' }}>{rest.description}</p>
                  )}
                  <p className="text-xs mt-0.5" style={{ color: '#A0A0A0' }}>Added {formatDate(rest.created_at)}</p>
                </div>
              </div>
              <button
                data-testid={`delete-restaurant-${i}`}
                onClick={() => setDeleteTarget(rest.id)}
                className="w-10 h-10 flex items-center justify-center rounded-xl touch-active"
                style={{ color: '#B93838' }}
                aria-label={`Delete ${rest.name}`}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Restaurant Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-2xl max-w-sm mx-auto" style={{ background: '#FFFFFF' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add Restaurant</DialogTitle>
            <DialogDescription>Add a dining spot on the Chileno Bay property.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-base font-medium mb-2 block" style={{ color: '#1A1D21' }}>Name</Label>
              <Input
                data-testid="new-restaurant-name"
                className="h-14 text-lg rounded-xl"
                style={{ background: '#F9F8F6', borderColor: '#E8E6E0' }}
                placeholder="e.g. La Palapa"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              />
            </div>
            <div>
              <Label className="text-base font-medium mb-2 block" style={{ color: '#1A1D21' }}>Description (optional)</Label>
              <Input
                data-testid="new-restaurant-desc"
                className="h-14 text-lg rounded-xl"
                style={{ background: '#F9F8F6', borderColor: '#E8E6E0' }}
                placeholder="e.g. Beachfront seafood"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
              />
            </div>
            <Button
              data-testid="save-restaurant-button"
              onClick={handleAdd}
              disabled={saving}
              className="w-full h-14 rounded-full text-lg font-medium touch-active"
              style={{ background: '#005A8D', color: '#FFFFFF' }}
            >
              {saving ? 'Saving...' : 'Add Restaurant'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="rounded-2xl" style={{ background: '#FFFFFF' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Delete Restaurant?</DialogTitle>
            <DialogDescription>This will remove the restaurant from your list. Existing meal logs won't be affected.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-2">
            <Button
              data-testid="cancel-delete-restaurant"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              className="flex-1 h-12 rounded-full"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-delete-restaurant"
              onClick={handleDelete}
              className="flex-1 h-12 rounded-full"
              style={{ background: '#B93838', color: '#FFFFFF' }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
